import { qrCodeCardModel } from '@/features/table-cards/api/TableCardsManagementAPI.model'
import IconButton from '@/shared/components/IconButton/IconButton'
import { useTableCardsQuery } from '@/features/table-cards/hooks/useTableCardsQuery'
import { useTableCardStore } from '@/features/table-cards/store/useTableCardStore'

import { rgbaStringToHsva } from '@uiw/color-convert'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './TableCardPreviewTile.module.scss'
import CheckBox from '../CheckBox/CheckBox'

interface TableCardPreviewTileProps {
  qrCard: qrCodeCardModel
  selectedTableCards: string[]
  setSelectedTableCards: Dispatch<SetStateAction<string[]>>
  setIsEditing: Dispatch<SetStateAction<boolean>>
  disableSelection?: boolean
  openModal: () => void
}

function TableCardPreviewTile(props: TableCardPreviewTileProps) {
  const {
    qrCard,
    selectedTableCards,
    setSelectedTableCards,
    setIsEditing,
    openModal,
    disableSelection
  } = props
  const { t } = useTranslation()
  const {
    id,
    title,
    description,
    shape,
    alignment = 'left',
    withNfc,
    languageCode,
    contentColor,
    contrastColor,
    backgroundColor,
    backgroundImage,
    width,
    height,
    diameter,
    tableIds
  } = qrCard

  const {
    setId,
    setTitle,
    setDescription,
    setShapeValue,
    setBackgroundColor,
    setContentColor,
    setContrastColor,
    setWithNfc,
    setLanguageCode,
    setAlignment,
    setBackgroundImage,
    setWidthValue,
    setHeightValue,
    setDiameterValue,
    setTableIds
  } = useTableCardStore()

  const { deleteTableCards } = useTableCardsQuery()

  const handleOnEditclick = () => {
    setId(id)
    setBackgroundColor(rgbaStringToHsva(backgroundColor))
    setContentColor(rgbaStringToHsva(contentColor))
    setContrastColor(rgbaStringToHsva(contrastColor))
    setWithNfc(!!withNfc)
    setLanguageCode(languageCode)
    setAlignment(alignment)
    setBackgroundImage(
      backgroundImage
        ? `${backgroundImage.url}?t=${backgroundImage.version}&alt=media`
        : null
    )
    setWidthValue(width || 0)
    setHeightValue(height || 0)
    setDiameterValue(diameter || 0)
    openModal()
    setShapeValue(shape)
    setAlignment(alignment)
    setIsEditing(true)
    setTableIds(tableIds)
    setTitle(title)
    setDescription(description)

    useTableCardStore.getState().setBaseline({
      id,
      tableNumber: 73,
      tableId: undefined,
      shapeValue: shape,
      backgroundColor: rgbaStringToHsva(backgroundColor),
      contentColor: rgbaStringToHsva(contentColor),
      contrastColor: rgbaStringToHsva(contrastColor),
      withNfc: !!withNfc,
      languageCode,
      alignment,
      backgroundImage: backgroundImage
        ? `${backgroundImage.url}?t=${backgroundImage.version}&alt=media`
        : null,
      widthValue: width || 0,
      heightValue: height || 0,
      diameterValue: diameter || 0,
      title,
      description,
      tableIds
    })
  }

  const handleSelectionChange = () => {
    if (selectedTableCards.includes(id)) {
      setSelectedTableCards(selectedTableCards.filter(cardId => cardId !== id))
    } else {
      setSelectedTableCards([...selectedTableCards, id])
    }
  }

  const isSelected = selectedTableCards.includes(id)

  return (
    <div
      className={`${styles.tableCardPreviewTileWrapper} ${
        isSelected ? styles.selected : ''
      } ${disableSelection ? styles.disabled : ''}`}
      style={{
        backgroundImage: backgroundImage
          ? `url(${backgroundImage.url}?t=${backgroundImage.version}&alt=media)`
          : undefined,
        backgroundColor: backgroundColor || 'transparent'
      }}
    >
      <div className={styles.tableCardPreviewLeftSideContainer}>
        <div className={styles.tableCardPreviewTileHeader}>
          <p className={`body-bold-medium ${styles.tableCardPreviewTileTitle}`}>
            {title}
          </p>
          <p
            className={`label-medium ${styles.tableCardPreviewTileDescription}`}
          >
            {description}
          </p>
        </div>
        <div className={styles.tableCardPreviewTileDescriptionWrapper}>
          {shape === 'circle' ? (
            <div className={styles.tableCardPreviewTileDescription}>
              <p className="label-medium">{t('TABLE_CARD_PREVIEW.DIAMETER')}</p>
              <p className="label-bold-medium">{diameter ?? '—'}mm</p>
            </div>
          ) : (
            <>
              <div className={styles.tableCardPreviewTileDescription}>
                <p className="label-medium">{t('TABLE_CARD_PREVIEW.WIDTH')}</p>
                <p className="label-bold-medium">{width ?? '—'}mm</p>
              </div>
              <div className={styles.tableCardPreviewTileDescription}>
                <p className="label-medium">{t('TABLE_CARD_PREVIEW.HEIGHT')}</p>
                <p className="label-bold-medium">{height ?? '—'}mm</p>
              </div>
            </>
          )}
          <div className={styles.tableCardPreviewTileDescription}>
            <p className="label-medium">
              {t('TABLE_CARD_PREVIEW.LANGUAGE_CODE')}
            </p>
            <p className="label-bold-medium">{languageCode}</p>
          </div>
        </div>
        <div className={styles.tableCardPreviewTileFooter}>
          <div className={styles.leftSideFooterContainer}>
            <div
              className={`${styles.tableCardPreviewTileShape} ${styles[shape]}`}
              style={{ backgroundColor: contentColor || 'transparent' }}
            />
            {alignment === 'left' && (
              <i
                className={`icon-big las la-align-left ${styles.tableCardPreviewTileIcon}`}
              />
            )}
            {alignment === 'center' && (
              <i
                className={`icon-big las la-align-center ${styles.tableCardPreviewTileIcon}`}
              />
            )}
          </div>
          {withNfc && <div className={styles.nfcPaymentIcon} />}
        </div>
      </div>
      <div className={styles.tableCardPreviewRightSideContainer}>
        {backgroundImage ? (
          <>
            <div
              className={styles.tableCardPreviewTileBackgroundOverlay}
              style={{ backgroundColor: contrastColor }}
            />
            <img
              className={styles.tableCardPreviewTileBackgroundImage}
              src={`${backgroundImage.url}?t=${backgroundImage.version}&alt=media`}
              alt="Background"
            />
          </>
        ) : (
          <div
            className={styles.tableCardPreviewTileBackgroundColor}
            style={{ backgroundColor: backgroundColor || 'transparent' }}
          />
        )}
        <div className={styles.tableCardPreviewCheckboxContainer}>
          <CheckBox
            onChange={handleSelectionChange}
            value={isSelected}
            disabled={disableSelection}
          />
        </div>

        <div className={styles.tableCardPreviewButtonsContainer}>
          <IconButton
            icon="las la-edit icon-medium"
            color="primary"
            onClick={handleOnEditclick}
          />
          <IconButton
            icon="las la-trash icon-medium"
            color="primary"
            onClick={() => deleteTableCards([id])}
          />
        </div>
      </div>
    </div>
  )
}

export default TableCardPreviewTile
